"use client";

import React from "react";
import { Building2, GraduationCap, Clock, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
  ProductsFilter,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


const current_wp_query_params =
  typeof window !== "undefined" && (window as any).wvcClient
    ? (window as any).wvcClient.getCurrentWPQueryParams()
    : {};
const wp_query = new WP_Query(current_wp_query_params);

function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div data-section-id="222209" className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} data-index={i} className="h-[400px] animate-pulse bg-muted/10" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-border p-12 text-center">
        <CircleAlert className="mb-4 size-8 text-destructive" />
        <p className="mb-4 text-muted-foreground font-mono text-sm">SYSTEM ERROR: FAILED TO LOAD ANALYTICS DATA.</p>
        <Button onClick={refetch} variant="outline" className="font-mono uppercase tracking-wider">
          Retry Scan
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-border p-12 text-center">
        <GraduationCap className="mb-4 size-12 text-muted-foreground/30" />
        <h3 className="mb-2 text-lg font-semibold font-default text-foreground">No TCA Reports Found</h3>
        <p className="text-muted-foreground font-mono text-xs">
          Adjust acreage filters or search parameters to locate specific timber stands.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {products.map((product, i) => {
          const firstCategory = product.categories?.[0];
          const providerName = product.customFields?.provider_name || "GTMX Labs";
          const credits = product.customFields?.credits || "LiDAR Verified";

          return (
            <Card
              key={product.id}
              data-index={i}
              className="group flex h-full flex-col overflow-hidden border-border/40 bg-card/40 shadow-sm transition-all hover:shadow-lg hover:border-primary/50 backdrop-blur-md rounded-sm"
            >
              {product.featured_image_url && (
                <Link to={product.link} className="relative aspect-video overflow-hidden bg-secondary">
                  <img
                    src={product.featured_image_url}
                    alt={product.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110 opacity-80 group-hover:opacity-100"
                    loading="lazy"
                  />
                  {firstCategory && (
                    <div className="absolute left-3 top-3">
                      <Badge variant="secondary" className="bg-background/80 backdrop-blur-md text-[10px] font-mono uppercase tracking-widest border-border/50">
                        {firstCategory.name}
                      </Badge>
                    </div>
                  )}
                </Link>
              )}

              <CardHeader className="flex-none p-5 pb-2">
                <div className="mb-2 flex items-center gap-2 text-[10px] font-mono text-muted-foreground uppercase tracking-tighter">
                  <Building2 className="size-3.5 text-accent-foreground" />
                  <span className="truncate">{providerName}</span>
                  <span className="mx-1 opacity-30">•</span>
                  <Clock className="size-3.5 text-accent-foreground" />
                  <span>{credits}</span>
                </div>
                <Link to={product.link}>
                  <CardTitle className="line-clamp-2 text-lg font-bold font-default leading-tight hover:text-accent-foreground transition-colors">
                    {product.title}
                  </CardTitle>
                </Link>
              </CardHeader>

              <CardContent className="flex-grow p-5 pt-2">
                {product.short_description && (
                  <div
                    className="line-clamp-2 text-sm text-muted-foreground font-default [&>p]:mb-0"
                    dangerouslySetInnerHTML={{ __html: product.short_description }}
                  />
                )}
              </CardContent>

              <CardFooter className="mt-auto flex items-center justify-between border-t border-border/20 p-5 bg-secondary/20">
                <div className="flex flex-col">
                  <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">Valuation</span>
                  {product.price !== undefined ? (
                    <span className="text-xl font-bold text-foreground font-mono">
                      ${product.price.toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-xl font-bold text-foreground font-mono">QUOTE</span>
                  )}
                </div>
                <div className="w-32">
                  <AddToCartButton
                    productId={product.id}
                    className="w-full"
                    buttonClassName="w-full bg-accent-foreground text-black font-bold uppercase text-xs tracking-widest hover:bg-accent-foreground/90 shadow-md rounded-sm transition-all"
                    text="Order TCA"
                    showQuantity={false}
                  />
                </div>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-8">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (hasPrev) prevPage();
                }}
                className={!hasPrev ? "pointer-events-none opacity-30" : "cursor-pointer font-mono text-xs uppercase tracking-widest hover:bg-secondary"}
              />
            </PaginationItem>
            <PaginationItem className="hidden sm:flex items-center px-4 text-xs font-mono text-muted-foreground uppercase tracking-widest">
              Sector {currentPage} / {totalPages}
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (hasNext) nextPage();
                }}
                className={!hasNext ? "pointer-events-none opacity-30" : "cursor-pointer font-mono text-xs uppercase tracking-widest hover:bg-secondary"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

export default function ShopContent() {
  return (
    <section id="shop-content" className="bg-background text-foreground py-12 md:py-16 lg:py-20">
      <div className="container mx-auto px-4 md:px-6">
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col gap-8 lg:flex-row">
            {}
            <aside className="w-full shrink-0 lg:w-1/4 lg:max-w-[280px]">
              <div className="sticky top-24 rounded-sm border border-border/30 bg-card/30 p-5 shadow-xl backdrop-blur-md">
                <h2 className="mb-4 text-xs font-bold font-mono uppercase tracking-[0.2em] text-accent-foreground">Acreage Filters</h2>
                <ProductsFilter />
              </div>
            </aside>

            {}
            <div className="flex w-full flex-col gap-6 lg:w-3/4">
              <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center border-b border-border/20 pb-6">
                <div>
                  <h1 className="text-3xl font-bold font-default tracking-tighter md:text-4xl text-foreground">
                    Timber Analysis Catalog
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-tight">
                    Select your stand size to generate a defensible LiDAR inventory.
                  </p>
                </div>
                <div className="w-full sm:w-auto">
                  <ProductSortDropdown label="Filter By" />
                </div>
              </div>

              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}